// A lookup's "+ New …": opens the object's New form over the current page and
// hands the created record back to the lookup, chosen. Mounted once in AppShell.
import { type FilterOption, RecordFormDialog } from '@olytic/foundation';
import { useEffect, useMemo, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { objects } from '../config/objects';
import type { ObjectKey } from '../navigation';
import { loadLookupOptions, optionLabel } from './lookupOptions';
import { objectMetaUrl, type ObjectMeta, type ObjectRow, parseObjectMetaResponse } from './objectsApi';
import { createBody, createRecord, formFields, NOT_WRITABLE } from './writes';

type Opener = (object: ObjectKey) => Promise<FilterOption | null>;
let opener: Opener | null = null;

/** Opens the New form for `object`; resolves with the new record, or null when cancelled. */
export function createLookupRecord(object: ObjectKey): Promise<FilterOption | null> {
  return opener ? opener(object) : Promise.resolve(null);
}

interface Pending {
  object: ObjectKey;
  resolve: (made: FilterOption | null) => void;
}

export function LookupCreateHost(): JSX.Element | null {
  const [pending, setPending] = useState<Pending | null>(null);
  const [meta, setMeta] = useState<ObjectMeta | null>(null);

  useEffect(() => {
    opener = (object) => new Promise((resolve) => {
      setMeta(null);
      setPending({ object, resolve });
    });
    return () => {
      opener = null;
    };
  }, []);

  useEffect(() => {
    if (!pending) return undefined;
    const controller = new AbortController();
    authenticatedFetch(objectMetaUrl(pending.object), { signal: controller.signal })
      .then(parseObjectMetaResponse)
      .then(
        (result) => {
          // Nothing to create here: hand nothing back.
          if (!result.writable?.create) {
            pending.resolve(null);
            setPending(null);
          } else setMeta(result);
        },
        () => {
          if (controller.signal.aborted) return;
          pending.resolve(null);
          setPending(null);
        },
      );
    return () => controller.abort();
  }, [pending]);

  const writable = meta?.writable ?? NOT_WRITABLE;
  const metadata = pending ? objects[pending.object] : undefined;
  const fields = useMemo(
    () => (metadata ? formFields(metadata, writable, loadLookupOptions) : []),
    [metadata, writable],
  );
  if (!pending || !metadata || !meta) return null;

  const close = (made: FilterOption | null) => {
    pending.resolve(made);
    setPending(null);
  };
  const singular = metadata.singular.toLowerCase();

  return (
    <RecordFormDialog
      open
      onOpenChange={(open) => { if (!open) close(null); }}
      title={`New ${singular}`}
      fields={fields}
      submitLabel="Save"
      onSubmit={async (values) => {
        const body = createBody(writable, values);
        const id = await createRecord(authenticatedFetch, pending.object, body);
        close({ value: id, label: optionLabel(pending.object, { id, ...body } as ObjectRow) });
      }}
    />
  );
}
